import type { AnalysisOutput, LinkedInProfile, UploadPreview } from '@smartapply/shared';

/**
 * Persistência client-side do snapshot do PDF + última análise + profile completo.
 *
 * Usa sessionStorage (vive apenas pela aba — fecha aba, limpa). Atende
 * CLAUDE.md §Privacidade: nunca em disco, só memória de sessão.
 *
 * Cuidado: o snapshotId aponta pra entrada in-memory no server (TTL 1h
 * renovado a cada acesso). Se o container do API reiniciar OU TTL passar,
 * o snapshot expira no server. Pra evitar reupload do PDF, guardamos o
 * profile completo aqui no client e enviamos junto com cada request que
 * precisa dele — o server reidrata o snapshot automaticamente.
 */

const KEY_PREVIEW = 'smartapply.uploadPreview';
const KEY_OUTPUT = 'smartapply.analysisOutput';
const KEY_MODEL = 'smartapply.analysisModelId';
const KEY_PROFILE = 'smartapply.profile';

export interface ProfileSession {
  preview: UploadPreview | null;
  output: AnalysisOutput | null;
  modelId: string | null;
  profile: LinkedInProfile | null;
}

function readJson<T>(key: string): T | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    // sessionStorage indisponível (modo privado em alguns browsers) ou JSON corrompido
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // quota cheia ou modo privado — falha silenciosa, app continua sem persistência
  }
}

function remove(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* idem */
  }
}

export function loadProfileSession(): ProfileSession {
  return {
    preview: readJson<UploadPreview>(KEY_PREVIEW),
    output: readJson<AnalysisOutput>(KEY_OUTPUT),
    modelId: readJson<string>(KEY_MODEL),
    profile: readJson<LinkedInProfile>(KEY_PROFILE),
  };
}

export function savePreview(preview: UploadPreview): void {
  writeJson(KEY_PREVIEW, preview);
  // Se o /upload devolveu profile completo, guarda separado pra mandar de
  // volta como fallback em rotas que precisam do profile (analyze, posts).
  if (preview.profile) {
    writeJson(KEY_PROFILE, preview.profile);
  }
  // Novo upload invalida análise antiga.
  remove(KEY_OUTPUT);
  remove(KEY_MODEL);
}

export function saveAnalysis(output: AnalysisOutput, modelId: string): void {
  writeJson(KEY_OUTPUT, output);
  writeJson(KEY_MODEL, modelId);
}

/**
 * Retorna o profile cacheado se houver, pra mandar como fallback no body
 * de rotas que precisam dele (analyze, posts/from-link, posts/from-topic).
 */
export function getCachedProfile(): LinkedInProfile | null {
  return readJson<LinkedInProfile>(KEY_PROFILE);
}

export function clearProfileSession(): void {
  remove(KEY_PREVIEW);
  remove(KEY_OUTPUT);
  remove(KEY_MODEL);
  remove(KEY_PROFILE);
}
